/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosImageFragment, HeroGlowFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";

import {
	BlurredImage,
	CosmosButtonProps,
	CosmosTextVariantEnum,
	LogoTitleDescButtonStack,
	OptimizedCosmosAssetFragment,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";

const styled = css({
	position: "relative",
	w: "full",
});

const glowWrap = css({
	position: "absolute",
	height: "256px",
	transform: "translateY(-56px)",
	w: "65%",
	right: "eds.dimension.128",
	zIndex: 0,

	xxlDown: {
		w: "75%",
		right: "eds.dimension.64",
	},

	xlDown: {
		w: "85%",
	},

	xsOnly: {
		right: "eds.dimension.24",
	},
});

const narrowStyles = css({
	maxWidth: `cosmos.article.sm`,
	marginInline: "auto",
	boxSizing: "content-box",
});

const ctaContentStyles = css({
	position: "relative",
	zIndex: 1,
	maxWidth: "600px",

	"& .logo img": {
		maxHeight: "32px",
		smDown: {
			maxHeight: "24px",
		},
	},

	"& .button-list": {
		smDown: {
			flexWrap: "wrap",
			alignItems: "flex-start",
		},
	},
});

type HeroGlowProps = {
	glowImageLarge?: boolean;
	logoImage?: Omit<CosmosImageFragment, "id"> | null;
	eyebrow?: string | null;
	modButtons?: CosmosButtonProps[] | null;
	disablePaddingBlockEnd?: boolean | null;
} & Pick<HeroGlowFragment, "blurredImage" | "title" | "desc" | "narrow" | "gradient">;

const glowImageLargeStyles = css({
	minH: "700px",
});

export const HeroGlow = ({
	blurredImage,
	title,
	desc,
	narrow,
	glowImageLarge,
	logoImage,
	eyebrow,
	modButtons,
	gradient,
	disablePaddingBlockEnd,
}: HeroGlowProps): React.JSX.Element => {
	const disablePaddingBlockEndResolved = disablePaddingBlockEnd ?? true;
	const width = 256;
	const height = 144;
	const dimensions = { width, height };

	//check that either title and desc are valid and have content besides whitespace
	const isValidHeading = Boolean(/\S/.test(title?.text || "") || /\S/.test(desc || ""));
	const hasCta = Boolean(logoImage?.url || eyebrow || modButtons?.length);

	const cosmosButtons = (modButtons || []).map(
		(button): CosmosButtonProps => ({
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "HeroGlow",
					section: "hero",
				},
			},
			...button,
		}),
	);

	return (
		<div className={cx(`hero-glow`, styled)}>
			<div className={cx(`hero-glow-wrap`, glowWrap, glowImageLarge ? glowImageLargeStyles : "")}>
				<BlurredImage
					image={{ ...blurredImage, ...dimensions } as OptimizedCosmosAssetFragment}
					blurPixels={200}
					distortionStrength={5}
					opacity={0.5}
					{...dimensions}
				/>
			</div>
			{hasCta ? (
				<ResponsiveContainer>
					<SectionContainer
						className={cx(narrow && narrowStyles)}
						disablePaddingBlockEnd={disablePaddingBlockEndResolved}
					>
						<div className={ctaContentStyles}>
							<LogoTitleDescButtonStack
								animate
								stack={{ gap: "eds.dimension.24", align: "start" }}
								image={logoImage}
								eyebrow={eyebrow}
								titleDescStack={{
									stack: { gap: "eds.dimension.24", align: "start" },
									title: {
										...title,
										as: "h1",
										variant: CosmosTextVariantEnum.heading2XL,
										color: "eds.text.contrast",
									},
									gradient,
									description: {
										html: desc,
										variant: CosmosTextVariantEnum.paragraphMD,
										as: "div",
										color: "eds.text.contrast",
									},
								}}
								buttons={cosmosButtons}
							/>
						</div>
					</SectionContainer>
				</ResponsiveContainer>
			) : isValidHeading ? (
				<ResponsiveContainer>
					<SectionContainer
						className={cx(narrow && narrowStyles)}
						disablePaddingBlockEnd={disablePaddingBlockEndResolved}
					>
						<SectionHeading title={{ ...title, as: "h1" }} desc={desc} />
					</SectionContainer>
				</ResponsiveContainer>
			) : null}
		</div>
	);
};
